import { jsx, jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, Children, useMemo } from "react";
import { bool, string, func, oneOfType, element, arrayOf, node } from "prop-types";
import cx from "classnames";
import Button from "@ingka/button";
import closeIcon from "@ingka/ssr-icon/paths/cross-small";
import WrapListener from "./utils/WrapListener";
import { calculateAutoDismissTime } from "@ingka/toast-shared";
const propTypes = {
  isOpen: bool,
  text: oneOfType([string, element]).isRequired,
  actionButtonText: string,
  prefix: string,
  className: string,
  actionClick: func,
  onCloseRequest: func,
  onTransitionEnd: func,
  children: oneOfType([
    arrayOf(node),
    node
  ]),
  ariaLabelCloseBtn: string
};
const calculateComponentTextLength = (component) => {
  if (typeof component === "string")
    return component.length;
  if (component.props && component.props.children) {
    return Children.map(
      component.props.children,
      (child) => calculateComponentTextLength(child)
    ).reduce((len, sum) => sum += len);
  }
  return 0;
};
function timeoutHook(callback, timeout) {
  const autoDismissRequest = useRef();
  const setDismissTimeout = () => {
    clearTimeout(autoDismissRequest.current);
    autoDismissRequest.current = setTimeout(callback, timeout);
  };
  const clearDismissTimeout = () => clearTimeout(autoDismissRequest.current);
  return [setDismissTimeout, clearDismissTimeout];
}
const Toast = (props) => {
  const {
    className,
    prefix = "",
    text = "",
    actionButtonText,
    isOpen = false,
    actionClick,
    onCloseRequest,
    onTransitionEnd,
    ariaLabelCloseBtn,
    children,
    ...other
  } = props;
  const baseClass = `${prefix}toast`;
  const dismissTime = calculateAutoDismissTime(calculateComponentTextLength(text));
  const [isOffScreen, setIsOffScreen] = useState(!isOpen);
  const [originallyFocusedElement, setOriginallyFocusedElement] = useState(null);
  const [setDismissTimeout, clearDismissTimeout] = timeoutHook(onCloseRequest, dismissTime);
  const toastWrapper = useRef(null);
  const customAction = useRef(null);
  const dismissAction = useRef(null);
  const toggleToast = cx(
    baseClass,
    isOpen && `${baseClass}--show`,
    isOffScreen && !isOpen && `${baseClass}--off-screen`,
    className
  );
  useEffect(() => {
    setOriginallyFocusedElement(document.activeElement);
    if (isOpen) {
      if (customAction && customAction.current && customAction.current !== document.currentTarget) {
        customAction.current.focus();
      } else {
        dismissAction.current.focus();
      }
    }
    return clearDismissTimeout;
  }, [isOpen, text]);
  const { onMouseOver, onTouchStart, onMouseLeave, onTouchEnd, onBlur, onFocus } = other;
  const [mouseOver, touchStart, mouseLeave, touchEnd, handleBlur, handleFocus] = useMemo(
    () => [
      WrapListener(clearDismissTimeout, onMouseOver),
      WrapListener(clearDismissTimeout, onTouchStart),
      WrapListener(setDismissTimeout, onMouseLeave),
      WrapListener(setDismissTimeout, onTouchEnd),
      WrapListener(setDismissTimeout, onBlur),
      WrapListener(clearDismissTimeout, onFocus)
    ],
    [onMouseOver, onTouchStart, onMouseLeave, onTouchEnd, onBlur, onFocus]
  );
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: toggleToast,
      tabIndex: "-1",
      "aria-hidden": !isOpen,
      role: actionButtonText ? "alertdialog" : "status",
      ...other,
      onMouseOver: mouseOver,
      onMouseLeave: mouseLeave,
      onTouchStart: touchStart,
      onTouchEnd: touchEnd,
      onFocus: handleFocus,
      onBlur: handleBlur,
      ref: toastWrapper,
      onTransitionEnd: (e) => {
        if (toastWrapper.current && e.propertyName === "transform" && e.target === toastWrapper.current) {
          setIsOffScreen(!isOpen);
          if (onTransitionEnd) {
            onTransitionEnd(e);
          }
        }
      },
      children: [
        /* @__PURE__ */ jsxs("div", { className: `${baseClass}__body-wrapper`, children: [
          /* @__PURE__ */ jsxs("div", { className: `${baseClass}__body`, children: [
            /* @__PURE__ */ jsx("p", { className: `${baseClass}__text`, children: text }),
            children
          ] }),
          actionButtonText && /* @__PURE__ */ jsx("div", { className: `${baseClass}__action`, onMouseDown: (e) => e.stopPropagation(), children: /* @__PURE__ */ jsx(
            Button,
            {
              small: true,
              ref: customAction,
              type: "plain",
              prefix,
              className: `${baseClass}__action-message`,
              text: actionButtonText,
              onClick: actionClick,
              tabIndex: !isOpen ? "-1" : null
            }
          ) })
        ] }),
        /* @__PURE__ */ jsx("div", { className: `${baseClass}__close-btn`, onMouseDown: (e) => e.stopPropagation(), children: /* @__PURE__ */ jsx(
          Button,
          {
            prefix,
            ref: dismissAction,
            iconOnly: true,
            type: "tertiary",
            inverseTheme: true,
            small: true,
            ssrIcon: closeIcon,
            onClick: (e) => {
              onCloseRequest(e);
              originallyFocusedElement.focus();
            },
            text: ariaLabelCloseBtn,
            tabIndex: !isOpen ? "-1" : null
          }
        ) })
      ]
    }
  );
};
Toast.propTypes = propTypes;
var Toast_default = Toast;
export {
  Toast_default as default
};
